import { BREAKPOINTS } from "@ubnt/store.core/lib/constants/layout";
import { Label } from "components/ProgressAnimationButton/ProgressAnimationButton.styles";
import { StyledContainer } from "components/StyledContainer";
import styled, { css } from "styled-components";
import { StyleableSvgImage } from "@ubnt/store.core/lib/components/StyleableSvgImage";
import { SlideDirection } from "./OverviewBrandPrinciples.types";

export const BrandPrincipleWrap = styled.div`
  direction: ltr;

  padding-top: 72px;

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    padding-top: 48px;
  }

  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    padding-top: 36px;
  }
`;

export const Container = styled(StyledContainer)`
  @media (max-width: 1700px) {
    padding-bottom: 24px;
  }

  @media ${BREAKPOINTS.DESKTOP_AND_DOWN} {
    padding: 24px 16px 8px;
  }
`;

export const StyledTitle = styled.h1`
  color: ${({ theme }) => theme.colors.text1};
  font-size: 42px;
  font-weight: bold;
  line-height: normal;
  margin: 0;
  text-align: center;
  text-wrap: balance;

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    font-size: 28px;
    margin-bottom: 24px;
  }
`;

export const TabContainer = styled.div`
  align-items: center;
  gap: 16px;
  display: flex;
  justify-content: center;

  ${Label} {
    br {
      display: none;
    }
  }
`;

export const ImageWrapper = styled.div`
  align-items: flex-end;
  display: flex;
  height: 100%;
  justify-content: flex-end;
  left: 0;
  position: absolute;
  top: 4%;
  width: 100%;

  @media ${BREAKPOINTS.DESKTOP_AND_DOWN} {
    display: none;
  }
`;

export const StyledImage = styled.img`
  height: 640px;
  max-width: 100%;
  object-fit: contain;
  object-position: top;
  position: absolute;
  right: 0;
  top: -20px;
  width: 100%;

  @media ${BREAKPOINTS.DESKTOP_AND_DOWN} {
    display: none;
  }
`;

export const MobileImageWrapper = styled.div`
  display: none;
  @media ${BREAKPOINTS.DESKTOP_AND_DOWN} {
    display: inline-block;
    position: relative;
    width: 100%;
  }
`;

export const MobileImage = styled.img`
  @media ${BREAKPOINTS.DESKTOP_AND_DOWN} {
    aspect-ratio: 1196 / 640;
    display: block;
    max-width: 100%;
    object-fit: contain;
    padding: 0 16px;
    text-align: center;
    width: 100%;
  }

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    height: auto;
    min-height: 256px;
    aspect-ratio: unset;
    width: 100%;
  }

  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    min-height: unset;
  }
`;

export const SlideContentContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  overflow: hidden;
  padding: 0 16px;

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    padding: 24px 0 0;
  }
`;

export const SlideContentInsideWrap = styled.div`
  max-height: 720px;
  max-width: 1920px;
  min-height: fit-content;
  position: relative;
  width: 100vw;
  padding: 30px 0 70px;
  margin-top: 20px;

  @media ${BREAKPOINTS.DESKTOP_AND_DOWN} {
    max-height: unset;
    padding: 16px 0 0;
  }

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    margin-top: 0;
    padding: 0;
  }
`;

export const RelativeContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;

  @media ${BREAKPOINTS.DESKTOP_AND_DOWN} {
    height: auto;
  }
`;

export const AnimationContainer = styled.div<{
  $isActive: boolean;
  $canAnimate: boolean;
  $slideDirection: SlideDirection;
}>`
  animation: ${({ $canAnimate, $slideDirection }) =>
    $canAnimate
      ? $slideDirection === SlideDirection.Right
        ? "slideFromRight 0.3s forwards"
        : "slideFromLeft 0.3s forwards"
      : "none"};
  height: 100%;
  left: 0;
  opacity: 0;
  position: absolute;
  display: flex;
  top: 0;
  justify-content: center;
  transform: translateX(0);
  width: 100%;
  z-index: 0;

  img {
    opacity: 0;
  }

  ${({ $isActive, $canAnimate, $slideDirection }) =>
    $isActive &&
    css`
      animation: ${$canAnimate
        ? $slideDirection === "right"
          ? "slideToLeft 0.3s forwards"
          : "slideToRight 0.3s forwards"
        : "none"};
      opacity: 1;
      position: static;
      transform: translateX(0);
      z-index: 10;

      img {
        opacity: 1;
      }
    `}

  @keyframes slideFromRight {
    0% {
      transform: translateX(0);
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
    100% {
      transform: translateX(0%);
      opacity: 0;
    }
  }

  @keyframes slideToLeft {
    0% {
      transform: translateX(10%);
      opacity: 0;
    }
    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes slideFromLeft {
    0% {
      transform: translateX(0);
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
    100% {
      transform: translateX(0%);
      opacity: 0;
    }
  }

  @keyframes slideToRight {
    0% {
      transform: translateX(-10%);
      opacity: 0;
    }
    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

export const SlideDescription = styled.p`
  color: ${({ theme }) => theme.colors.neutral08};
  font-size: 16px;
  line-height: 24px;
  margin: 0;

  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    font-size: 14px;
  }
`;

export const SlideContainer = styled.div`
  border-radius: 6px;
  height: 100%;
  max-width: 1200px;
  padding: 32px;
  width: 100%;

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    padding: 16px;
    border-radius: 0;
  }
`;

export const VideoBackground = styled.video`
  height: 100%;
  left: 0;
  object-fit: unset;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: -1;
`;

export const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  max-width: 406px;

  @media ${BREAKPOINTS.DESKTOP_AND_DOWN} {
    max-width: 100%;
  }

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    gap: 16px;
  }
`;

export const SlideHeader = styled.div`
  border-bottom: 1px solid #dee0e3;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 16px;

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    gap: 8px;
  }
`;

export const SlideChapter = styled.span`
  color: ${({ theme }) => theme.colors.ublue06};
  font-size: 18px;
  font-weight: 700;
  line-height: normal;
`;

export const SlideTitle = styled.h3`
  color: ${({ theme }) => theme.colors.text1};
  font-size: 28px;
  font-style: normal;
  font-weight: 700;
  line-height: 36px;
  margin: 0;

  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    font-size: 20px;
    line-height: normal;
  }
`;

export const SlidesListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media ${BREAKPOINTS.DESKTOP_AND_DOWN} {
    gap: 24px;
  }
`;

export const SlideText = styled.p`
  color: #50565e;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  margin: 0;

  @media ${BREAKPOINTS.DESKTOP_AND_DOWN} {
    br {
      display: none;
    }
  }

  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    font-size: 14px;
  }
`;

export const SlideItem = styled.div<{ $isActive: boolean }>`
  align-items: center;
  display: flex;
  gap: 16px;
  width: 100%;

  svg {
    color: ${({ theme }) => theme.colors.neutral08};
    flex-shrink: 0;
    height: 40px;
    width: 40px;

    @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
      height: 28px;
      width: 28px;
    }
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      ${SlideText} {
        color: ${({ theme }) => theme.colors.ublue06};
      }

      svg {
        color: ${({ theme }) => theme.colors.ublue06};
      }
    `}
`;

const CONTENT_BREAKDOWN_BREAKPOINT = `(max-width: 1200px)`;

export const ButtonWrapper = styled.div`
  @media ${CONTENT_BREAKDOWN_BREAKPOINT} {
    display: none;
  }
`;

export const StatusBarContainer = styled.button`
  display: none;

  @media ${CONTENT_BREAKDOWN_BREAKPOINT} {
    background: rgba(0, 0, 0, 0.2);
    border: none;
    outline: none;
    border-radius: 4px;
    display: flex;
    height: 3px;
    width: 100%;
    position: relative;
    width: 72px;
    overflow: hidden;
  }

  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    width: 64px;
  }
`;

export const StyledStatusBar = styled.div<{
  $isActive: boolean;
  $playbackLimit: number;
}>`
  border-radius: 4px;
  height: 3px;
  position: absolute;
  top: 0;
  transform: translateX(-100%);
  width: 100%;

  ${({ $isActive, $playbackLimit }) =>
    $isActive &&
    css`
      animation: progressLinePrinciples ${$playbackLimit}s linear;
      animation-fill-mode: forwards;
      background: ${({ theme }) => theme.colors.black};
    `}

  @keyframes progressLinePrinciples {
    0% {
      transform: translateX(-100%);
    }

    100% {
      transform: translateX(0);
    }
  }
`;

export const InvisibleButton = styled.button<{ $isRight?: boolean }>`
  background: transparent;
  border: none;
  display: none;
  height: calc(100% - 16px);
  left: 0;
  outline: none;
  position: absolute;
  top: 16px;
  width: calc(44px + 12px + 8px);
  z-index: 15;

  ${({ $isRight }) =>
    $isRight &&
    css`
      left: unset;
      right: 0;
    `}

  @media ${BREAKPOINTS.DESKTOP_AND_DOWN} {
    display: block;
  }

  @media ${BREAKPOINTS.TABLET_AND_DOWN} {
    top: 0;
    height: 100%;
  }
`;

export const MobileOnly = styled.div`
  display: none;
  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    display: block;
  }
`;

export const DesktopOnly = styled.div`
  @media ${BREAKPOINTS.MOBILE_AND_DOWN} {
    display: none;
  }
`;

export const ArrowButtonLeft = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  background: transparent;
  cursor: pointer;
  padding: 0;
`;

export const ArrowButtonRight = styled(ArrowButtonLeft)`
  left: auto;
  right: 12px;
`;

export const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(2px);
`;

export const ButtonIcon = styled(StyleableSvgImage)``;
